/* External */
import {
  memo, useEffect, useCallback, useState, useMemo, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { useLocation, useHistory, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import CardCreate from '@dtvgo/card-create-profile';

/* Other */
import { getJsonImage } from 'src/theme/images';

/* Components */
import FocusableButtonProfile from '../../../../components/FocusableButtonProfile';
import FocusableEditNameProfile from '../../../../components/FocusableEditNameProfile';
import FocusableEditAllowContentProfile from '../../../../components/FocusableEditAllowContentProfile';

/* Utils */
import {
  colors,
  AgeRatingBR,
  TYPE_ACTIONS_PROFILE,
  BR_AGE_RATING_DATA,
  BR_GET_LAST_RATING,
} from '../../../../utils/constants';
import { Routes, getLink } from '../../../../utils/router';
import { useOnBackPress } from '../../../../utils/keyDownEvents';
import {
  getKeyByValue,
  getInitials,
  formatName,
  featureFlagParentalControl,
  getAgeRating,
  checkUnratedContent,
  isProfileOptionsChanged,
} from '../../../../utils/profiles';
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';
import { useCountry } from '../../../../providers/ConfigProvider';

/* Asset */
import chevronLeft from '../../../../assets/svg/chevron-left.svg';

/* GTM */
import { GTM_CATEGORIES, GTM_EVENT_NAMES, GTM_SUBCATEGORIES } from '../../../../utils/googleTags';

/* Storage */
import { editProfile } from '../../../../storage/profiles/actions';
import { editProfileAPI, createProfileAPI } from '../../../../services/profiles';

/* Style */
import './CreateProfileResume.scss';

function CreateProfileResume({ realFocusKey, setFocus }) {
  const nameProfileFocusKey = `${realFocusKey}-name-profile-resume`;
  const allowContentProfileFocusKey = `${realFocusKey}-allow-content-profile-resume`;
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const params = useParams();
  const country = useCountry();
  const hasParentalControl = featureFlagParentalControl(country);
  const userProfiles = useSelector((state) => state.profile.profiles) ?? JSON.parse(localStorage.getItem('profiles'));
  const userProfilesAgeRating = useSelector((state) => state.profile.saveProfileAgeRating);
  const [profiles] = useState(userProfiles);
  const [name, setName] = useState('');
  const [ageRating, setAgeRating] = useState(
    getKeyByValue(BR_AGE_RATING_DATA, AgeRatingBR.DEFAULT_AGE),
  );
  const [isUnratedContentChecked, setIsUnratedContentChecked] = useState(true);
  const cardColor = params.id
    ? colors[location.state?.indexColor]
    : colors[(profiles?.length || 0) + 1];
  const indexColor = location.state?.indexColor;
  const typeCreateName = location.state?.typeCreateName;
  const { commonTaggingEvent } = useGtmLocalContext();
  const userNameSplit = getInitials(name);
  const isCreatingProfile = useRef(false);
  const tagsMountRef = useRef(true);

  const profileData = useMemo(() => (
    {
      name: formatName(params.name),
      ageRating: (userProfilesAgeRating?.ageRating ?? ageRating),
      isUnratedContentChecked: checkUnratedContent(userProfilesAgeRating)
        ? userProfilesAgeRating?.isUnratedContentChecked
        : isUnratedContentChecked,
    }
  ), [
    ageRating,
    params,
    userProfilesAgeRating,
    isUnratedContentChecked,
  ]);

  useEffect(() => {
    setName(profileData.name);
    setAgeRating(profileData.ageRating);
    setIsUnratedContentChecked(profileData.isUnratedContentChecked);
  }, [profileData]);

  useEffect(() => {
    localStorage.setItem('username', name);
    setFocus(nameProfileFocusKey);
  }, [
    nameProfileFocusKey,
    setFocus,
    name,
  ]);

  const profileCommonTagEvent = useCallback(({ subCategory, eventName }) => {
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PROFILES_DATA,
        subCategory: subCategory || GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE_NAME,
        eventName,
      });
    }
  }, [commonTaggingEvent]);

  useEffect(() => {
    if (tagsMountRef.current && country) {
      profileCommonTagEvent({
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
      });
      tagsMountRef.current = false;
    }
  }, [country, profileCommonTagEvent]);

  const validationButton = useCallback(() => (
    !name
    || name.replace(/\s/g, '').length === 0
  ), [name]);

  const clickCreateProfile = useCallback(
    () => {
      const changeParentalControl = isProfileOptionsChanged(
        getAgeRating(ageRating),
        isUnratedContentChecked,
      );
      if (!(validationButton() === false && !isCreatingProfile.current)) return;

      isCreatingProfile.current = true;
      profileCommonTagEvent(getEventNameAndSubCategory(typeCreateName));

      if (!name) return;

      const editedData = {
        profile_id: params.id,
        profile_custom_age: getAgeRating(ageRating || BR_GET_LAST_RATING),
        profile_unrated_content: isUnratedContentChecked,
      };
      if (params.id && typeCreateName !== TYPE_ACTIONS_PROFILE.ONBOARDING) {
        editProfileAPI(params.id, {
          name,
          ...(hasParentalControl && { ageRating: getAgeRating(ageRating) }),
          ...(hasParentalControl && {
            isNotRatedContentAllowed: isUnratedContentChecked,
          }),
        }).then((res) => {
          dispatch(editProfile(res.data.profiles));
          localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
          history.push(getLink(Routes.Private.Profiles.Edit, {
            id: params.id,
            changeParentalControl,
            ...(changeParentalControl && { profileEditedData: editedData }),
          }), { indexColor });
          return true;
        }).catch((error) => {
          console.error(error);
          history.push(getLink(Routes.Private.Profiles.Error), {
            source: 'MIDDLEWARE',
            location: 'PROFILE_EDIT',
            type: 'UNEXPECTED',
            externCode: error?.response?.status,
            typeError: TYPE_ACTIONS_PROFILE.EDIT,
            parentalControl: hasParentalControl,
            changeParentalControl,
            ...(changeParentalControl && { profileEditedData: editedData }),
          });
          return false;
        });
      } else if (typeCreateName !== TYPE_ACTIONS_PROFILE.ONBOARDING) {
        createProfileAPI({
          name,
          avatar: '',
          ...(hasParentalControl && { ageRating: getAgeRating(ageRating) }),
          ...(hasParentalControl && { isNotRatedContentAllowed: isUnratedContentChecked }),
        }).then((res) => {
          dispatch(editProfile(res.data.profiles));
          localStorage.setItem('profiles', JSON.stringify(res.data.profiles));
          history.push(getLink(Routes.Private.Profiles), {
            create: true,
            changeParentalControl,
            ...(changeParentalControl && { profileEditedData: editedData }),
          });
          return true;
        }).catch((error) => {
          console.error(error);
          history.push(getLink(Routes.Private.Profiles.Error), {
            source: 'MIDDLEWARE',
            location: 'PROFILE_EDIT',
            type: 'UNEXPECTED',
            externCode: error?.response?.status,
            typeError: TYPE_ACTIONS_PROFILE.EDIT,
            parentalControl: hasParentalControl,
            changeParentalControl,
            ...(changeParentalControl && { profileEditedData: editedData }),
          });
          return false;
        });
      } else if (profiles?.length > 0) {
        history.push(getLink(Routes.Private.Profiles.Create), { username: name });
      } else {
        history.push(getLink(Routes.Private.Profiles.Create.Confirmation), {
          username: name,
        });
      }
    },
    [
      dispatch,
      history,
      indexColor,
      name,
      ageRating,
      isUnratedContentChecked,
      params.id,
      profiles?.length,
      profileCommonTagEvent,
      typeCreateName,
      validationButton,
      hasParentalControl,
    ],
  );

  const handleCancel = useCallback(() => {
    localStorage.removeItem('username');

    profileCommonTagEvent({
      subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
      eventName: GTM_EVENT_NAMES.PROFILES_CREATE.CANCEL_PROFILES_CREATE,
    });

    history.push(
      getLink(Routes.Private.Profiles),
    );
  }, [history, profileCommonTagEvent]);

  const editProfileName = useCallback(() => {
    profileCommonTagEvent({
      subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
      eventName: GTM_EVENT_NAMES.PROFILES_CREATE.EDIT_NAME_PROFILE,
    });

    history.push(
      getLink(Routes.Private.Profiles.Create.Name),
      { typeCreateName: TYPE_ACTIONS_PROFILE.CREATE },
    );
  }, [history, profileCommonTagEvent]);

  const editProfileAllowedContent = useCallback(() => {
    profileCommonTagEvent({
      eventName: GTM_EVENT_NAMES.PROFILES_CREATE.CHANGE_ALLOWED_CONTENT_PROFILE,
      subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
    });
    history.push(getLink(Routes.Private.Profiles.Create.AllowedContent), {
      typeCreateAgeRating: TYPE_ACTIONS_PROFILE.CREATE,
    });
  }, [history, profileCommonTagEvent]);

  useOnBackPress({
    listener: () => {
      profileCommonTagEvent({
        subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
        eventName: GTM_EVENT_NAMES.PROFILES_CREATE.BACK_PROFILES_CREATE,
      });

      history.goBack();
    },
  });

  return (
    <div className="dtv-html5-c-user-profile-create">
      <div className="dtv-html5-c-user-profile-create--div">
        <div className="dtv-html5-c-title__back">
          <img
            src={getJsonImage('chevron-left') || chevronLeft}
            alt="Chevron Left"
            className="dtv-html5-c-title__back--chevronLeft"
          />
          <h1><FormattedMessage id="profile.create" /></h1>
        </div>
        <div className="dtv-html5-c-user-profile-create--row">
          <div className="dtv-html5-c-user-profile-create--row-col_profile">
            <div className="dtv-html5-c-user-profile-create--row-col_profile-div">
              <CardCreate
                color={cardColor}
                isInputNotEmpty={userNameSplit}
              />
            </div>
          </div>
          <div className="dtv-html5-c-user-profile-create--row-col_data">
            <div className="dtv-html5-c-user-profile-create--row-section">
              <div className="dtv-html5-c-user-profile-create--row-col_data_title">
                <h2 className="dtv-html5-c-user-profile-create--row-col_data_title_h2">
                  <FormattedMessage id="common.data" />
                </h2>
              </div>
              <FocusableEditNameProfile
                focusKey={nameProfileFocusKey}
                profile={name}
                onEnterPress={editProfileName}
                focused
              />
              {hasParentalControl && (
                <>
                  <div className="dtv-html5-c-user-profile-create--row-col_data_title">
                    <h2 className="dtv-html5-c-user-profile-create--row-col_data_title_h2--marginTop">
                      <FormattedMessage id="profiles.parentalControl.title" />
                    </h2>
                  </div>
                  <FocusableEditAllowContentProfile
                    indexColor={cardColor}
                    focusKey={allowContentProfileFocusKey}
                    onEnterPress={editProfileAllowedContent}
                    userProfilesAgeRating={userProfilesAgeRating}
                    algo="creation prop"
                  />
                </>
              )}
            </div>

            <div className="dtv-html5-c-user-profile-create--button">
              <FocusableButtonProfile
                className={`dtv-html5-c-user-profile-create--button-pattern ${validationButton() ? 'dtv-btn-tv dtv-html5-c-create-profile__button' : 'dtv-btn-blue dtv-html5-c-create-profile__button'}`}
                secondGray
                bordered
                focused
                disabled={validationButton()}
                onEnterPress={clickCreateProfile}
              >
                <FormattedMessage id="profile.create" />
              </FocusableButtonProfile>
              <FocusableButtonProfile
                secondGray
                bordered
                focused
                indexColor={location.state.indexColor}
                onEnterPress={handleCancel}
              >
                <FormattedMessage id="common.not.now" />
              </FocusableButtonProfile>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

CreateProfileResume.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};
CreateProfileResume.defaultProps = {};

export default memo(withFocusable()(memo(CreateProfileResume)));

function getEventNameAndSubCategory(actionType) {
  if (actionType === TYPE_ACTIONS_PROFILE.EDIT) {
    return {
      eventName: GTM_EVENT_NAMES.PROFILES_CREATE_NAME.CONTINUE_PROFILE_EDIT,
      subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE_NAME,
    };
  }

  if (actionType === TYPE_ACTIONS_PROFILE.ONBOARDING) {
    return {
      eventName: GTM_EVENT_NAMES.PROFILES_ONBOARDING.CONTINUE_CREATE_NAME_ONBOARDING,
      subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_ONBOARDING,
    };
  }

  return {
    subCategory: GTM_SUBCATEGORIES.PROFILE.PROFILES_CREATE,
    eventName: GTM_EVENT_NAMES.PROFILES_CREATE.SELECT_CREATE_PROFILE,
  };
}
